import { Badge } from '@/components/ui/badge';
import type { Policy } from '@mls-wallet/shared-types';
import {
  type PolicyWithChange,
  getPolicyState,
} from '../utils/policyProposalUtils';
import { cn, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Pencil } from 'lucide-react';
import { getPolicyTypeLabel } from '../utils';

function VerticalDivider() {
  return <div className="h-auto self-stretch w-[1px] bg-border"></div>;
}
function MetadataItem({
  label,
  value,
  isChanged,
  changeType,
}: {
  label?: string;
  value: string;
  isChanged?: boolean;
  changeType?: 'added' | 'removed';
}) {
  return (
    <div
      className={cn(
        'py-0 px-0 gap-1',
        isChanged && changeType === 'removed' && 'bg-red-50 dark:bg-red-950/20',
        isChanged &&
          changeType === 'added' &&
          'bg-green-50 dark:bg-green-950/20'
      )}
    >
      {label && (
        <>
          <span
            className={cn(
              'text-xs font-normal  text-muted-foreground',
              isChanged &&
                changeType === 'removed' &&
                'text-red-700 dark:text-red-400',
              isChanged &&
                changeType === 'added' &&
                'text-green-700 dark:text-green-400'
            )}
          >
            {label}
          </span>
          &nbsp;&nbsp;
        </>
      )}
      <span
        className={cn(
          'text-sm font-medium',
          isChanged &&
            changeType === 'removed' &&
            'text-red-700 dark:text-red-400',
          isChanged &&
            changeType === 'added' &&
            'text-green-700 dark:text-green-400'
        )}
      >
        {value}
      </span>
    </div>
  );
}

interface PolicyHeaderProps {
  policy: Policy | PolicyWithChange;
  showEditButton?: boolean;
  onEditClick?: () => void;
  className?: string;
  isInEditMode?: boolean;
  changes?: {
    name?: 'added' | 'removed';
    description?: 'added' | 'removed';
    action?: 'added' | 'removed';
    createdAt?: 'added' | 'removed';
    updatedAt?: 'added' | 'removed';
  };
}

export function PolicyHeader({
  policy,
  showEditButton,
  onEditClick,
  className,
  isInEditMode,
  changes,
}: PolicyHeaderProps) {
  const renderEditStateBadge = (policy: PolicyWithChange) => {
    const state = getPolicyState(policy);

    if (state === 'new') {
      return (
        <Badge
          variant="secondary"
          className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
        >
          New
        </Badge>
      );
    }

    if (state === 'modified') {
      return (
        <Badge
          variant="secondary"
          className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
        >
          Modified
        </Badge>
      );
    }

    if (state === 'deleted') {
      return (
        <Badge
          variant="secondary"
          className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
        >
          Deleted
        </Badge>
      );
    }

    // Default to unmodified
    return (
      <Badge
        variant="secondary"
        className="bg-gray-100 text-gray-600 dark:bg-gray-800/20 dark:text-gray-400"
      >
        Unmodified
      </Badge>
    );
  };

  return (
    <div className={cn('gap-4 border-b pb-4', className)}>
      <div className="flex flex-col gap-2">
        <div className="text-xs text-muted-foreground font-normal uppercase tracking-wide">
          Policy
        </div>

        <div className="flex flex-col gap-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1
                  className={cn(
                    'text-xl font-semibold',
                    changes?.name === 'removed' &&
                      'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 px-2 py-1 rounded',
                    changes?.name === 'added' &&
                      'bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400 px-2 py-1 rounded'
                  )}
                >
                  {policy.name}
                </h1>
              </div>
              <div className="flex flex-row items-center justify-between gap-2">
                <div
                  className={cn(
                    'text-muted-foreground',
                    changes?.description === 'removed' &&
                      'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 px-2 py-1 rounded',
                    changes?.description === 'added' &&
                      'bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400 px-2 py-1 rounded'
                  )}
                >
                  {policy.description}
                </div>
                {showEditButton && onEditClick && (
                  <Button onClick={onEditClick} variant="ghost" size="sm">
                    <Pencil className="h-4 w-4" />
                    Edit policy
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full border-t flex flex-row items-stretch justify-between py-4 -mb-6 gap-4 mt-4">
        <div className="flex flex-row items-center gap-4">
          <MetadataItem
            value={getPolicyTypeLabel(policy.type)}
            isChanged={!!changes?.action}
            {...(changes?.action ? { changeType: changes.action } : {})}
          />
          <VerticalDivider />
          <MetadataItem
            label="Updated"
            value={formatDate(policy.updatedAt)}
            isChanged={!!changes?.updatedAt}
            {...(changes?.updatedAt ? { changeType: changes.updatedAt } : {})}
          />
          <VerticalDivider />
          <MetadataItem
            label="Created"
            value={formatDate(policy.createdAt)}
            isChanged={!!changes?.createdAt}
            {...(changes?.createdAt ? { changeType: changes.createdAt } : {})}
          />
        </div>

        {/* Status badge - only show in edit mode */}
        {isInEditMode && (
          <div className="flex flex-row items-center">
            {renderEditStateBadge(policy)}
          </div>
        )}
      </div>
    </div>
  );
}
